/**
 * Vorbello P2P WebRTC Mesh Manager (Perfect Negotiation & Robust ICE Handling)
 * Provides 100% reliable peer-to-peer audio, video, chat & screen sharing directly between devices
 * via signal.php on app.vorbello.eu.
 */

export interface P2PParticipant {
  identity: string;
  name: string;
  stream?: MediaStream;
  isMuted?: boolean;
  isCamOff?: boolean;
}

export interface P2PCallbacks {
  onParticipantConnected?: (identity: string, name: string) => void;
  onParticipantDisconnected?: (identity: string) => void;
  onRemoteTrack?: (identity: string, name: string, track: MediaStreamTrack, stream: MediaStream) => void;
  onChatMessage?: (msg: any) => void;
  onFileTransferProgress?: (info: {
    transferId: string;
    progress: number;
    name: string;
    size: number;
    senderName: string;
    senderIdentity: string;
    timestamp: string;
    text: string;
    isComplete: boolean;
  }) => void;
  onDataMessage?: (data: any) => void;
}

interface PeerEntry {
  pc: RTCPeerConnection;
  name: string;
  dc?: RTCDataChannel;
  remoteStream: MediaStream;
  pendingCandidates: RTCIceCandidateInit[];
  isMakingOffer: boolean;
}

export class P2PMeshManager {
  public peerId: string;
  public displayName: string;
  public roomSlug: string;
  public localStream: MediaStream | null = null;
  public isCameraEnabled: boolean = true;
  public isMicEnabled: boolean = true;
  public isScreenShareEnabled: boolean = false;

  private peers: Map<string, PeerEntry> = new Map();
  private pollInterval: any = null;
  private isConnected: boolean = false;
  private callbacks: P2PCallbacks = {};
  private signalBaseUrl: string = typeof window !== 'undefined' && window.location.protocol === 'file:' 
    ? 'https://app.vorbello.eu/signal.php' 
    : '/signal.php';

  constructor(opts: {
    peerId: string;
    displayName: string;
    roomSlug: string;
    callbacks: P2PCallbacks;
    signalBaseUrl?: string;
  }) {
    this.peerId = opts.peerId;
    this.displayName = opts.displayName;
    this.roomSlug = opts.roomSlug;
    this.callbacks = opts.callbacks;
    if (opts.signalBaseUrl) this.signalBaseUrl = opts.signalBaseUrl;
  }

  public async start(localStream?: MediaStream | null): Promise<boolean> {
    if (localStream) {
      this.localStream = localStream;
    }

    try {
      // 1. Join room on signaling server
      const res = await fetch(`${this.signalBaseUrl}?action=join&room=${encodeURIComponent(this.roomSlug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'join',
          room: this.roomSlug,
          peerId: this.peerId,
          name: this.displayName,
        }),
      });

      const data = await res.json();
      console.info('[Vorbello P2P] Joined room via signal.php. Active peers:', data.peers);

      this.isConnected = true;

      // 2. Connect to existing peers using deterministic initiator rule (smaller ID initiates)
      if (Array.isArray(data.peers)) {
        for (const remotePeer of data.peers) {
          if (remotePeer.peerId !== this.peerId) {
            const shouldInitiate = this.peerId < remotePeer.peerId;
            await this.initiatePeerConnection(remotePeer.peerId, remotePeer.name, shouldInitiate);
          }
        }
      }

      // 3. Start fast heartbeat polling for incoming signals and active peers every 700ms
      this.startPolling();
      return true;
    } catch (err) {
      console.warn('[Vorbello P2P] Failed to start P2P signaling:', err);
      return false;
    }
  }

  private startPolling() {
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.pollInterval = setInterval(async () => {
      if (!this.isConnected) return;
      try {
        const res = await fetch(
          `${this.signalBaseUrl}?action=poll&room=${encodeURIComponent(this.roomSlug)}&peerId=${encodeURIComponent(this.peerId)}`
        );
        const data = await res.json();

        // 1. Handle incoming signals (offers, answers, ICE candidates)
        if (Array.isArray(data.signals)) {
          for (const sig of data.signals) {
            await this.handleIncomingSignal(sig.from, sig.payload);
          }
        }

        // 2. Handle active peers list
        if (Array.isArray(data.peers)) {
          const remotePeerIds = new Set(data.peers.map((p: any) => p.peerId));

          // Discover newly joined peers
          for (const p of data.peers) {
            if (p.peerId !== this.peerId && !this.peers.has(p.peerId)) {
              // Deterministic initiator rule based on ID comparison to prevent glare
              const shouldInitiate = this.peerId < p.peerId;
              await this.initiatePeerConnection(p.peerId, p.name, shouldInitiate);
            }
          }

          // Clean up departed peers
          for (const [existingId, peerObj] of this.peers.entries()) {
            if (!remotePeerIds.has(existingId)) {
              console.info('[Vorbello P2P] Peer departed:', existingId);
              peerObj.pc.close();
              this.peers.delete(existingId);
              this.callbacks.onParticipantDisconnected?.(existingId);
            }
          }
        }
      } catch (e) {
        // network hiccup, ignore
      }
    }, 700);
  }

  private async initiatePeerConnection(remotePeerId: string, remoteName: string, isInitiator: boolean) {
    if (this.peers.has(remotePeerId)) return;

    console.info(`[Vorbello P2P] Setting up PeerConnection with ${remoteName} (${remotePeerId}), initiator: ${isInitiator}`);

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:19302' },
        { urls: 'stun:global.stun.twilio.com:3478' },
      ],
    });

    const remoteStream = new MediaStream();
    const peerEntry: PeerEntry = {
      pc,
      name: remoteName,
      remoteStream,
      pendingCandidates: [],
      isMakingOffer: false,
    };
    this.peers.set(remotePeerId, peerEntry);

    pc.oniceconnectionstatechange = () => {
      console.info(`[Vorbello P2P] ICE state with ${remoteName} (${remotePeerId}): ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'failed') {
        try { pc.restartIce(); } catch (_) {}
      }
    };

    // Add local tracks if available
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        try {
          pc.addTrack(track, this.localStream);
        } catch (_) {}
      }
    }

    // Handle outgoing ICE Candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignal(remotePeerId, {
          type: 'candidate',
          candidate: event.candidate.toJSON(),
        });
      }
    };

    // Handle incoming Remote Tracks
    pc.ontrack = (event) => {
      console.info(`[Vorbello P2P] ontrack: ${event.track.kind} from ${remoteName} (${remotePeerId})`);
      remoteStream.addTrack(event.track);
      const streamToPass = (event.streams && event.streams[0]) ? event.streams[0] : remoteStream;
      this.callbacks.onRemoteTrack?.(remotePeerId, remoteName, event.track, streamToPass);
      this.callbacks.onParticipantConnected?.(remotePeerId, remoteName);
    };

    // DataChannel for instant chat and reactions
    if (isInitiator) {
      const dc = pc.createDataChannel('vorbello-p2p-dc');
      this.setupDataChannel(dc, remotePeerId);
      peerEntry.dc = dc;

      peerEntry.isMakingOffer = true;
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await this.sendSignal(remotePeerId, {
          type: 'offer',
          sdp: offer,
          senderName: this.displayName,
        });
      } catch (err) {
        console.warn('[Vorbello P2P] Error creating offer:', err);
      } finally {
        peerEntry.isMakingOffer = false;
      }
    } else {
      pc.ondatachannel = (e) => {
        this.setupDataChannel(e.channel, remotePeerId);
        peerEntry.dc = e.channel;
      };
    }
  }

  private setupDataChannel(dc: RTCDataChannel, remotePeerId: string) {
    dc.onopen = () => {
      console.info(`[Vorbello P2P] DataChannel open with peer ${remotePeerId}`);
    };
    dc.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.type === 'chat') {
          this.callbacks.onChatMessage?.(parsed.message);
        } else if (parsed.type === 'chat_file_chunk') {
          this.handleIncomingFileChunk(parsed);
        } else {
          this.callbacks.onDataMessage?.(parsed);
        }
      } catch (e) {
        console.warn('[Vorbello P2P] DataChannel parse error:', e);
      }
    };
  }

  private async handleIncomingSignal(fromPeerId: string, payload: any) {
    if (!payload || !payload.type) return;

    let peerEntry = this.peers.get(fromPeerId);
    const isPolite = this.peerId > fromPeerId;

    if (payload.type === 'offer') {
      const senderName = payload.senderName || 'Participant';
      if (!peerEntry) {
        await this.initiatePeerConnection(fromPeerId, senderName, false);
        peerEntry = this.peers.get(fromPeerId);
      }
      if (!peerEntry) return;

      const offerCollision = peerEntry.isMakingOffer || peerEntry.pc.signalingState !== 'stable';
      if (offerCollision && !isPolite) {
        console.info(`[Vorbello P2P] Glare detected with ${fromPeerId}; ignoring offer (we are impolite).`);
        return;
      }

      try {
        if (offerCollision && isPolite) {
          console.info(`[Vorbello P2P] Glare detected with ${fromPeerId}; rolling back local offer (we are polite).`);
          await peerEntry.pc.setLocalDescription({ type: 'rollback' } as any);
        }

        await peerEntry.pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));

        // Flush pending ICE candidates now that remote description is set!
        if (peerEntry.pendingCandidates.length > 0) {
          for (const cand of peerEntry.pendingCandidates) {
            try {
              await peerEntry.pc.addIceCandidate(new RTCIceCandidate(cand));
            } catch (_) {}
          }
          peerEntry.pendingCandidates = [];
        }

        const answer = await peerEntry.pc.createAnswer();
        await peerEntry.pc.setLocalDescription(answer);
        await this.sendSignal(fromPeerId, {
          type: 'answer',
          sdp: answer,
        });
      } catch (err) {
        console.warn('[Vorbello P2P] Error handling offer:', err);
      }
    } else if (payload.type === 'answer') {
      if (peerEntry && peerEntry.pc.signalingState !== 'stable') {
        try {
          await peerEntry.pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));

          // Flush pending ICE candidates!
          if (peerEntry.pendingCandidates.length > 0) {
            for (const cand of peerEntry.pendingCandidates) {
              try {
                await peerEntry.pc.addIceCandidate(new RTCIceCandidate(cand));
              } catch (_) {}
            }
            peerEntry.pendingCandidates = [];
          }
        } catch (err) {
          console.warn('[Vorbello P2P] Error setting remote description for answer:', err);
        }
      }
    } else if (payload.type === 'candidate') {
      if (payload.candidate) {
        if (peerEntry && peerEntry.pc.remoteDescription && peerEntry.pc.remoteDescription.type) {
          try {
            await peerEntry.pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
          } catch (err) {
            console.warn('[Vorbello P2P] Error adding ICE candidate:', err);
          }
        } else if (peerEntry) {
          peerEntry.pendingCandidates.push(payload.candidate);
        }
      }
    }
  }

  private async sendSignal(toPeerId: string, payload: any) {
    try {
      await fetch(`${this.signalBaseUrl}?action=signal&room=${encodeURIComponent(this.roomSlug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'signal',
          room: this.roomSlug,
          from: this.peerId,
          to: toPeerId,
          payload,
        }),
      });
    } catch (err) {
      console.warn('[Vorbello P2P] Send signal error:', err);
    }
  }

  private incomingFileTransfers = new Map<string, {
    chunks: string[];
    receivedCount: number;
    totalChunks: number;
    metadata: any;
    timeoutId: any;
  }>();

  private handleIncomingFileChunk(data: any) {
    const transferId = data.transferId;
    if (!transferId || typeof data.chunkIndex !== 'number' || !data.totalChunks) return;

    let transfer = this.incomingFileTransfers.get(transferId);
    if (!transfer) {
      transfer = {
        chunks: new Array(data.totalChunks),
        receivedCount: 0,
        totalChunks: data.totalChunks,
        metadata: {
          name: data.name || 'document',
          size: data.size || 0,
          fileType: data.fileType || 'application/octet-stream',
          text: data.text || '',
          timestamp: data.timestamp || new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
          senderName: data.senderName || 'Participant',
          senderIdentity: data.senderIdentity || '',
        },
        timeoutId: null,
      };
      this.incomingFileTransfers.set(transferId, transfer);
    }

    if (transfer.timeoutId) clearTimeout(transfer.timeoutId);
    transfer.timeoutId = setTimeout(() => {
      this.incomingFileTransfers.delete(transferId);
    }, 60000);

    if (!transfer.chunks[data.chunkIndex]) {
      transfer.chunks[data.chunkIndex] = data.data || '';
      transfer.receivedCount++;
    }

    const progress = Math.min(100, Math.round((transfer.receivedCount / transfer.totalChunks) * 100));

    this.callbacks.onFileTransferProgress?.({
      transferId,
      progress,
      name: transfer.metadata.name,
      size: transfer.metadata.size,
      senderName: transfer.metadata.senderName,
      senderIdentity: transfer.metadata.senderIdentity,
      timestamp: transfer.metadata.timestamp,
      text: transfer.metadata.text,
      isComplete: transfer.receivedCount === transfer.totalChunks,
    });

    if (transfer.receivedCount === transfer.totalChunks) {
      if (transfer.timeoutId) clearTimeout(transfer.timeoutId);
      const fullDataUrl = transfer.chunks.join('');
      this.incomingFileTransfers.delete(transferId);

      const chatMsg = {
        id: transferId,
        senderName: transfer.metadata.senderName,
        senderIdentity: transfer.metadata.senderIdentity,
        text: transfer.metadata.text,
        timestamp: transfer.metadata.timestamp,
        isSelf: false,
        file: {
          name: transfer.metadata.name,
          size: transfer.metadata.size,
          type: transfer.metadata.fileType,
          dataUrl: fullDataUrl,
          progress: 100,
        },
      };

      this.callbacks.onChatMessage?.(chatMsg);
    }
  }

  public async sendFileMessage(
    file: { name: string; size: number; type: string; dataUrl: string },
    text?: string,
    messageId?: string
  ): Promise<void> {
    const transferId = messageId || `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const CHUNK_SIZE = 8192;
    const dataUrl = file.dataUrl || '';
    const totalChunks = Math.max(1, Math.ceil(dataUrl.length / CHUNK_SIZE));
    const timestamp = new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

    for (let i = 0; i < totalChunks; i++) {
      const chunkData = dataUrl.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const chunkPayload = JSON.stringify({
        type: 'chat_file_chunk',
        transferId,
        chunkIndex: i,
        totalChunks,
        name: file.name,
        size: file.size,
        fileType: file.type,
        text: (text || '').trim(),
        timestamp,
        senderName: this.displayName,
        senderIdentity: this.peerId,
        data: chunkData,
      });

      for (const peerObj of this.peers.values()) {
        if (peerObj.dc && peerObj.dc.readyState === 'open') {
          try {
            peerObj.dc.send(chunkPayload);
          } catch (_) {}
        }
      }

      if (i % 2 === 0 && i < totalChunks - 1) {
        await new Promise(r => setTimeout(r, 6));
      }
    }
  }

  public broadcastDataMessage(data: any) {
    const payload = JSON.stringify(data);
    for (const peerObj of this.peers.values()) {
      if (peerObj.dc && peerObj.dc.readyState === 'open') {
        try {
          peerObj.dc.send(payload);
        } catch (_) {}
      }
    }
  }

  public updateLocalStream(newStream: MediaStream) {
    this.localStream = newStream;
    for (const peerObj of this.peers.values()) {
      const senders = peerObj.pc.getSenders();
      for (const track of newStream.getTracks()) {
        const sender = senders.find(s => s.track && s.track.kind === track.kind);
        if (sender) {
          sender.replaceTrack(track).catch(() => {});
        } else {
          try {
            peerObj.pc.addTrack(track, newStream);
          } catch (_) {}
        }
      }
    }
  }

  public leave() {
    this.isConnected = false;
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }

    try {
      fetch(`${this.signalBaseUrl}?action=leave&room=${encodeURIComponent(this.roomSlug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'leave',
          room: this.roomSlug,
          peerId: this.peerId,
        }),
      }).catch(() => {});
    } catch (_) {}

    for (const peerObj of this.peers.values()) {
      try {
        peerObj.pc.close();
      } catch (_) {}
    }
    this.peers.clear();
  }
}
